import { Control, useFieldArray, useWatch } from 'react-hook-form';
import StyledButton from '../styled/StyledButton';
import { useState } from 'react';
import StyledTextarea from '../styled/StyledTextarea';
import StyledInput from '../styled/StyledInput';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { useTranslation } from '@/i18n';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';

interface CircuitAdditionalParagraphsProps {
  control: Control<CircuitFormDataWithTranslations>;
  isViewMode?: boolean;
}

const CircuitAdditionalParagraphs = ({
  control,
  isViewMode = false,
}: CircuitAdditionalParagraphsProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additionalParagraphs',
  });

  return (
    <>
      {fields.map((field, indexField) => (
        <CircuitAdditionalParagraph
          key={field.id}
          index={indexField}
          control={control}
          removeParagraph={() => remove(indexField)}
          isViewMode={isViewMode}
        />
      ))}
      {!isViewMode && fields.length < 2 && (
        <StyledButton
          variant="link"
          className="text-brand-600 p-0 w-fit"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
          title={t('circuits.additionalParagraphs.addParagraph')}
          onClick={() => append({ title: '', content: undefined })}
        />
      )}
    </>
  );
};

export default CircuitAdditionalParagraphs;

const CircuitAdditionalParagraph = ({
  control,
  index,
  removeParagraph,
  isViewMode = false,
}: {
  control: Control<CircuitFormDataWithTranslations>;
  index: number;
  removeParagraph: () => void;
  isViewMode?: boolean;
}) => {
  const { t } = useTranslation();
  const paragraph = useWatch({
    control,
    name: `additionalParagraphs.${index}`,
  });

  const [isParagraphAdded, setIsParagraphAdded] = useState(
    paragraph.content !== undefined,
  );

  return !isParagraphAdded ? (
    !isViewMode && (
      <div className="flex items-end">
        <StyledInput
          control={control}
          name={`additionalParagraphs.${index}.title`}
          placeholder={t(
            'circuits.additionalParagraphs.paragraphTitlePlaceholder',
          )}
          title={t('circuits.additionalParagraphs.paragraphTitle')}
        />
        <StyledButton
          variant="link"
          title={t('circuits.additionalParagraphs.addParagraphButton')}
          onClick={() => setIsParagraphAdded(true)}
        />
      </div>
    )
  ) : (
    <StyledTextarea
      control={control}
      name={`additionalParagraphs.${index}.content`}
      title={paragraph.title}
      titleIcon={
        !isViewMode ? (
          <TrashIcon
            className="size-5 stroke-brand-600 hover:cursor-pointer"
            onClick={removeParagraph}
          />
        ) : undefined
      }
      rows={3}
      disabled={isViewMode}
    />
  );
};
